/**
 * DIYEditor - 亲子自制绘本故事编辑器
 *
 * 路由: /create/diy/editor/:storyId
 */
import { useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Rocket,
  Loader2,
  AlertCircle,
  CheckCircle,
  PenLine,
} from 'lucide-react';
import { useDIYEditorStore } from '../../store/useDIYEditorStore';
import PageNav from './components/PageNav';
import PageEditor from './components/PageEditor';
import CharacterPanel from './components/CharacterPanel';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function DIYEditor() {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    storyTitle,
    pages,
    cover,
    characters,
    currentPageIndex,
    hasUnsavedChanges,
    isLoading,
    isSaving,
    saveError,
    setStoryId,
    initFromStory,
    updatePage,
    updateCover,
    setCurrentPage,
    markSaved,
    setLoading,
    setSaving,
    setSaveError,
    getTextContent,
    validateAllPages,
  } = useDIYEditorStore();

  // 初始化：加载故事数据
  useEffect(() => {
    if (!storyId) return;

    setStoryId(storyId);
    setLoading(true);

    const loadStory = async () => {
      try {
        const token = localStorage.getItem('token');

        // 并行获取 Story 配置和文案内容
        const [configRes, textRes] = await Promise.all([
          fetch(`${API_BASE_URL}/stories/${storyId}/config`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/text`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const configResult = await configRes.json();
        // DIY 模式下文案可能尚未生成，允许 text 接口 404
        let textContent = null;
        if (textRes.ok) {
          try {
            const textResult = await textRes.json();
            textContent = textResult.success ? textResult.data?.textContent : null;
          } catch {
            textContent = null;
          }
        }

        if (configResult.success && configResult.data) {
          const supportingChars = configResult.data.supportingCharacters || [];
          initFromStory(configResult.data, textContent, supportingChars);
        } else {
          setSaveError('加载故事数据失败');
          setLoading(false);
        }
      } catch (err) {
        console.error('[DIYEditor] Load story failed:', err);
        setSaveError('加载故事数据失败，请检查网络');
        setLoading(false);
      }
    };

    loadStory();

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [storyId]);

  // 自动保存
  const doSave = useCallback(
    async (showLoading = false) => {
      if (!storyId) return;

      if (showLoading) setSaving(true);
      setSaveError(null);

      try {
        const token = localStorage.getItem('token');
        const textContent = getTextContent();

        const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/text`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            pages: textContent.pages,
            cover: textContent.cover,
          }),
        });

        const result = await response.json();

        if (result.success) {
          markSaved();
        } else {
          setSaveError(result.error || '保存失败');
        }
      } catch (err) {
        console.error('[DIYEditor] Auto-save failed:', err);
        setSaveError('保存失败，请检查网络');
      } finally {
        if (showLoading) setSaving(false);
      }
    },
    [storyId, getTextContent]
  );

  // 防抖自动保存
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      doSave(false);
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, pages, cover, doSave]);

  // 手动保存
  const handleManualSave = async () => {
    await doSave(true);
  };

  // 完成创作
  const handleComplete = async () => {
    const { valid, missing } = validateAllPages();

    if (!valid) {
      // 滚动到第一个缺失的页面
      const firstMissing = missing[0];
      if (firstMissing === 0) {
        setCurrentPage(-1);
      } else {
        setCurrentPage(firstMissing - 1);
      }
      // 显示提示（通过 local state 或简单 alert）
      const missingNames = missing
        .map((m) => (m === 0 ? '封面' : `第 ${m} 页`))
        .join('、');
      alert(`以下内容尚未填写，请补充后再完成创作：\n${missingNames}`);
      return;
    }

    // 先保存
    await doSave(true);

    // 更新 story stage
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/next`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn('[DIYEditor] Stage transition failed:', err);
      // 非阻塞，继续跳转
    }

    // 跳转到音频生成
    navigate(`/create/stage/2?storyId=${storyId}`);
  };

  // 返回向导
  const handleBack = () => {
    // 尝试保存草稿
    if (hasUnsavedChanges) {
      doSave(false);
    }
    navigate(`/create?storyId=${storyId}&returnStep=3&flow=diy`);
  };

  // 当前是否封面
  const isCover = currentPageIndex === -1;

  // 当前页面数据
  const currentPage = isCover ? null : pages[currentPageIndex];

  // 封面是否已填写
  const coverFilled = !!cover.illustrationPrompt.trim();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          <p className="text-sm text-slate-500">正在加载编辑器...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* 顶部栏 */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PenLine className="w-5 h-5 text-amber-500" />
            <h1 className="text-sm sm:text-base font-bold text-slate-800 truncate max-w-[200px] sm:max-w-md">
              {storyTitle || '亲子自制绘本'}
            </h1>
            <span className="hidden sm:inline text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              DIY 模式
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* 保存状态 */}
            {isSaving ? (
              <span className="flex items-center gap-1.5 text-xs text-indigo-600">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                保存中...
              </span>
            ) : hasUnsavedChanges ? (
              <span className="flex items-center gap-1.5 text-xs text-amber-600">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                有未保存的更改
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600">
                <CheckCircle className="w-3.5 h-3.5" />
                已保存
              </span>
            )}

            {/* 保存按钮 */}
            <button
              onClick={handleManualSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              保存
            </button>
          </div>
        </div>

        {/* 错误提示 */}
        {saveError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-rose-50 border-t border-rose-100"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
              <p className="text-xs text-rose-700">{saveError}</p>
            </div>
          </motion.div>
        )}
      </header>

      {/* 主体内容 */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-4">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 h-full">
          {/* 左侧栏 */}
          <aside className="lg:w-56 xl:w-64 shrink-0">
            <div className="lg:sticky lg:top-20 space-y-4">
              {/* 页面导航 */}
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  页面导航
                </h3>
                <PageNav
                  pages={pages}
                  coverFilled={coverFilled}
                  currentPageIndex={currentPageIndex}
                  onSelectPage={setCurrentPage}
                />
              </div>

              {/* 角色面板 */}
              <div className="bg-white rounded-xl border border-slate-200 p-3 hidden lg:block">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  故事角色
                </h3>
                <CharacterPanel characters={characters} />
              </div>

              {/* 进度统计 */}
              <div className="bg-white rounded-xl border border-slate-200 p-3 hidden lg:block">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  完成进度
                </h3>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">封面</span>
                    <span className={coverFilled ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
                      {coverFilled ? '已完成' : '待填写'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">故事页</span>
                    <span className="text-slate-700 font-medium">
                      {pages.filter((p) => p.content.trim() && p.illustrationPrompt.trim()).length} / {pages.length}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-indigo-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.round(
                          ((pages.filter((p) => p.content.trim() && p.illustrationPrompt.trim()).length +
                            (coverFilled ? 1 : 0)) /
                            (pages.length + 1)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* 右侧编辑区 */}
          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
              {isCover ? (
                <PageEditor
                  page={{ pageNumber: 0, content: '', illustrationPrompt: cover.illustrationPrompt }}
                  isCover
                  coverTitle={cover.title}
                  coverAuthor={cover.author}
                  characters={characters}
                  onUpdate={(data) => {
                    if (data.illustrationPrompt !== undefined) {
                      updateCover({ illustrationPrompt: data.illustrationPrompt });
                    }
                  }}
                  onUpdateCover={(data) => {
                    updateCover(data);
                  }}
                />
              ) : currentPage ? (
                <PageEditor
                  page={currentPage}
                  characters={characters}
                  onUpdate={(data) => updatePage(currentPageIndex, data)}
                />
              ) : null}
            </div>
          </main>
        </div>
      </div>

      {/* 底部操作栏 */}
      <footer className="bg-white border-t border-slate-200 sticky bottom-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回向导
          </button>

          <div className="flex items-center gap-3">
            {/* 移动端保存按钮 */}
            <button
              onClick={handleManualSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="sm:hidden inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-slate-100 text-slate-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              保存
            </button>

            <motion.button
              onClick={handleComplete}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-shadow"
            >
              <Rocket className="w-4 h-4" />
              完成创作，进入音频生成
            </motion.button>
          </div>
        </div>
      </footer>
    </div>
  );
}
